import { deriveUploadStep } from '@src/components/Dashboard/uploadStatus';
import useUploadPhase from '@src/components/Dashboard/user-story/useUploadPhase';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import type { UploadStepInfo } from '@src/components/Dashboard/uploadStatus';
import type { StoryUploadPhase } from '@src/stores-v2/UserStoryUploadFlowStore';
import type { DashboardStory } from '@src/types.ts/story';
import type { MutableRefObject } from 'react';

/**
 * storyId → id of the pending progress toast currently up for that story.
 * Shared with UploadCompletionToaster, which swaps that toast for the
 * "is ready" outcome in place instead of queueing a second one.
 */
export type ProgressToastIds = MutableRefObject<Map<string, number>>;

export const uploadProgressTitle = (
  story: Pick<DashboardStory, 'title'>,
  phase: StoryUploadPhase,
): string => {
  if (story.title) return story.title;
  if ('fileName' in phase && phase.fileName) return phase.fileName;
  return 'Your video';
};

/** Same wording as the Story Manager row's status line, minus the percent —
 *  the toast's bar carries that. */
export const uploadProgressMessage = (
  title: string,
  step: UploadStepInfo,
): string =>
  `${title} · ${step.label}${
    step.done ? '' : ` (${step.stepNumber}/${step.totalSteps})`
  }`;

type Props = {
  story: DashboardStory;
  progressToastIds: ProgressToastIds;
};

/**
 * Keeps one `pending` toast with a progress bar up for a story uploaded this
 * session while the user is away from Story Manager, so the bar they left
 * behind on the story's row follows them around the app. Renders nothing
 * itself — it drives the shared toast queue:
 *
 * - mount: show the toast; every phase/percent change updates it in place
 * - the user closes it (or newer toasts push it out): stop, don't re-open
 * - upload failure: take it down — the Story Manager row owns Retry / Remove
 * - unmount (back on Story Manager, story finished): take it down unless the
 *   parent already swapped it for the "is ready" outcome
 */
const UploadProgressToast = observer(({ story, progressToastIds }: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { phase, percent } = useUploadPhase(story);
  const step = deriveUploadStep(story, phase);
  const failed = phase.phase === 'error' || !!step.error;
  const message = uploadProgressMessage(
    uploadProgressTitle(story, phase),
    step,
  );
  const progress = Math.round(percent ?? 0);

  const toastIdRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  // Read the queue during render (not only in the effect) so a dismissal — the
  // toast leaving the queue — is an observable change that re-renders us and
  // re-runs the effect below; the effect itself re-reads the queue fresh.
  const queue = videoCreator.toasts;

  useEffect(() => {
    if (stoppedRef.current) return;

    const stop = () => {
      progressToastIds.current.delete(story.id);
      toastIdRef.current = null;
      stoppedRef.current = true;
    };

    if (failed) {
      if (toastIdRef.current !== null) {
        videoCreator.dismissToast(toastIdRef.current);
      }
      stop();
      return;
    }

    if (toastIdRef.current === null) {
      const id = videoCreator.showToast({
        state: 'pending',
        message,
        progress,
      });
      toastIdRef.current = id;
      progressToastIds.current.set(story.id, id);
      return;
    }

    // The parent took the toast over (swapped it for the ready outcome), or the
    // user closed it: either way it is no longer ours to update. Read the queue
    // fresh here — the render-time read only exists to re-run this effect.
    const id = toastIdRef.current;
    const current = videoCreator.toasts.find(toast => toast.id === id);
    if (!current || progressToastIds.current.get(story.id) !== id) {
      stop();
      return;
    }

    if (current.message !== message || current.progress !== progress) {
      videoCreator.updateToast(id, { state: 'pending', message, progress });
    }
  }, [
    videoCreator,
    story.id,
    progressToastIds,
    message,
    progress,
    failed,
    queue,
  ]);

  // Clearing the ref matters under StrictMode's mount → cleanup → mount: the
  // re-run must see "no toast yet" and show a fresh one, not "ours went
  // missing" and stop.
  useEffect(
    () => () => {
      const id = toastIdRef.current;
      if (id !== null && progressToastIds.current.get(story.id) === id) {
        videoCreator.dismissToast(id);
        progressToastIds.current.delete(story.id);
      }
      toastIdRef.current = null;
    },
    [videoCreator, story.id, progressToastIds],
  );

  return null;
});

export default UploadProgressToast;
